<script>
  import Router, { link, push, location } from 'svelte-spa-router';
  import { onMount } from 'svelte';
  import { authStore, checkAuthStatus } from './stores/auth.js';
  import { _, isLoading as i18nLoading } from './lib/i18n.js';
  import { isCustomer, isPartner, loadUserRoles, userRoles } from './stores/roles.js';
  import { PAYROLL_ENABLED } from './lib/features.js';

  // Import page components
  import Login from './routes/Login.svelte';
  import Home from './routes/Home.svelte';
  import Customers from './routes/Customers.svelte';
  import CustomerDetail from './routes/CustomerDetail.svelte';
  import Products from './routes/Products.svelte';
  import Proposals from './routes/Proposals.svelte';
  import Settings from './routes/Settings.svelte';
  import ChangePassword from './routes/ChangePassword.svelte';
  import Reports from './routes/Reports.svelte';
  import ComplianceReports from './routes/ComplianceReports.svelte';
  import AdminTokenPanel from './routes/AdminTokenPanel.svelte';
  import Partners from './routes/Partners.svelte';
  import PartnerDetail from './routes/PartnerDetail.svelte';
  import MyPartner from './routes/MyPartner.svelte';
  import Enrollment from './routes/Enrollment.svelte';
  import Signatures from './routes/Signatures.svelte';
  import Timesheets from './routes/Timesheets.svelte';
  import OAuthConsent from './routes/OAuthConsent.svelte';
  import ForgotPassword from './routes/ForgotPassword.svelte';
  import ResetPassword from './routes/ResetPassword.svelte';
  import VerifyEmailPending from './routes/VerifyEmailPending.svelte';
  import Logout from './routes/Logout.svelte';
  import { consentResumeUrl, safeOAuthReturnTo } from './lib/oauthReturnTo.js';
  import { API_ROOT } from './stores/api.js';
  import {
    ADDRESS_CONFIRMATION_ROUTE,
    isAdminOnlyRoute,
    isBlockedForPartner,
    isPublicRoute,
    mustConfirmAddress,
  } from './lib/routeAccess.js';

  import Payroll from './routes/Payroll.svelte';
  import CustomerTimesheets from './routes/CustomerTimesheets.svelte';
  import Catalogo from './routes/Catalogo.svelte';

  // Define routes
  const routes = {
    '/': Home,
    '/login': Login,
    '/forgot-password': ForgotPassword,
    '/reset-password': ResetPassword,
    '/verify-email-pending': VerifyEmailPending,
    '/logout': Logout,
    '/customers': Customers,
    '/customers/:id': CustomerDetail,
    '/products': Products,
    '/proposals': Proposals,
    '/reports': Reports,
    '/compliance-reports': ComplianceReports,
    '/admin/tokens': AdminTokenPanel,
    '/partners': Partners,
    '/partners/:id': PartnerDetail,
    '/my-partner': MyPartner,
    '/enrollment': Enrollment,
    '/firma': Signatures,
    '/timesheets': Timesheets,
    '/my-timesheets': CustomerTimesheets,
    '/catalogo': Catalogo,
    // Not admin-only: any internal role may authorize an MCP client for itself.
    '/oauth/consent': OAuthConsent,
    // Payroll (cedolini) is offline by default — see lib/features.js.
    ...(PAYROLL_ENABLED ? { '/payroll': Payroll } : {}),
    '/settings': Settings,
    '/change-password': ChangePassword,
  };

  let isLoading = true;
  let rolesLoaded = false;

  /**
   * Whether the current URL carries a returnTo that Login will actually follow.
   *
   * Validated with the same helper Login uses, not merely tested for presence:
   * a `returnTo` pointing somewhere else is rejected there, so treating it as
   * "Login owns the navigation" would leave the user stranded on /login.
   *
   * @returns {boolean}
   */
  function hasOAuthReturnTo() {
    if (typeof window === 'undefined') return false;
    return Boolean(safeOAuthReturnTo(window.location.search, API_ROOT));
  }

  /**
   * Send an unauthenticated visitor to the login page.
   *
   * The consent route needs special care. Its challenge lives in the pre-hash
   * query string (the router is hash-based, so Hydra's parameters cannot travel
   * in the hash), and a bare `push('/login')` would leave the user signed in on
   * the home page with a half-finished authorization request behind them. So the
   * challenge is rewritten into a `returnTo` that walks the handshake back
   * through the backend, using `replaceState` to avoid a reload.
   *
   * @param {string} currentPath Route the guard is rejecting
   */
  function sendToLogin(currentPath) {
    if (currentPath === '/oauth/consent' && typeof window !== 'undefined') {
      const resumeUrl = consentResumeUrl(window.location.search, API_ROOT);
      if (resumeUrl) {
        const url = new URL(window.location.href);
        // Add returnTo, keep consent_challenge. Replacing the whole query
        // string would drop the challenge, so going Back to the consent screen
        // would land on a page that cannot say what it is asking about.
        url.searchParams.set('returnTo', resumeUrl);
        window.history.replaceState(window.history.state, '', url);
      }
    }
    push('/login');
  }

  onMount(async () => {
    await checkAuthStatus();
    if ($authStore.isAuthenticated) {
      await loadUserRoles();
      rolesLoaded = true;
    }
    isLoading = false;
  });

  // A sign-in made in this page (the login form, not a reload) also needs the
  // roles before the role-based branches of the guard below can run; a
  // sign-out forgets them.
  let rolesRequested = false;
  $: if (!isLoading && $authStore.isAuthenticated && !rolesLoaded && !rolesRequested) {
    rolesRequested = true;
    loadUserRoles().finally(() => { rolesLoaded = true; });
  }
  $: if (!isLoading && !$authStore.isAuthenticated) {
    rolesLoaded = false;
    rolesRequested = false;
  }

  // Route guard reactive statement
  $: {
    if (!isLoading && !$i18nLoading) {
      const currentPath = $location;

      if (!$authStore.isAuthenticated && !isPublicRoute(currentPath)) {
        sendToLogin(currentPath);
      } else if ($authStore.isAuthenticated && currentPath === '/login') {
        // After login, redirect to home — unless the login page is carrying a
        // returnTo, in which case Login.svelte owns the navigation and pushing
        // here would race it.
        if (!hasOAuthReturnTo()) {
          push('/');
        }
      } else if ($authStore.isAuthenticated && rolesLoaded
        && mustConfirmAddress({ isCustomer: $isCustomer, emailVerified: $authStore.user?.emailVerified, path: currentPath })) {
        // A customer whose address is not verified confirms it by setting a
        // new password through the mailed link; the portal API refuses it
        // (403 email_unverified) until then.
        push(ADDRESS_CONFIRMATION_ROUTE);
      } else if ($authStore.isAuthenticated && rolesLoaded && currentPath === ADDRESS_CONFIRMATION_ROUTE
        && $authStore.user?.emailVerified !== false) {
        push('/');
      } else if ($authStore.isAuthenticated && rolesLoaded) {
        // Partner first, and deliberately so: a customer promoted to reseller
        // holds BOTH roles, and the server resolves its scope through the
        // partner lens. Testing the customer branch first would bounce it off
        // the very pages that lens exists to open.
        if ($isPartner) {
          if (isBlockedForPartner(currentPath)) {
            console.log('[App] Partner blocked from admin route:', currentPath);
            push('/');
          }
        } else if ($isCustomer && isAdminOnlyRoute(currentPath)) {
          console.log('[App] Customer blocked from admin route:', currentPath);
          push('/');
        }
      }
    }
  }
</script>

{#if isLoading || $i18nLoading}
  <div class="min-h-screen flex items-center justify-center">
    <div class="text-center">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
      <p class="mt-4 text-gray-600">{$i18nLoading ? 'Caricamento...' : $_('common.loading')}</p>
    </div>
  </div>
{:else}
  <Router {routes} />
{/if}